import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, RotateCcw, Sparkles, CalendarCheck, MapPin, Wallet } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import { waLink } from "@/api";
import { ATTRACTIONS } from "@/staticData";
import { toast } from "sonner";

export default function Quiz({ config, units }) {
  const { t, lang } = useLang();
  const questions = t("quiz.questions");
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState([]);
  const [consent, setConsent] = useState(true);

  const answer = (idx) => {
    const next = [...answers.slice(0, step), idx];
    setAnswers(next);
    if (step < questions.length - 1) setStep(step + 1);
    else {
      setStep(questions.length);
    }
  };

  const compute = (a) => {
    const score = a[0] + a[1];
    let nights = score <= 1 ? 2 : score === 2 ? 3 : 4;
    if (a[3] > 0) nights += 1;
    if (a[7] === 2) nights = Math.max(3, nights);
    if (a[6] <= 1) nights = Math.max(3, nights);
    nights = Math.min(5, Math.max(1, nights));
    const unitType = a[2] >= 2 ? "house" : "room";
    return { nights, unitType };
  };

  const result = step === questions.length ? compute(answers) : null;

  const rankedCats = (a) => {
    const w = {};
    const add = (c, n) => { w[c] = (w[c] || 0) + n; };
    if (a[2] === 0) add("solo", 2);
    if (a[2] === 2) { add("nightlife", 2); add("solo", 1); }
    if (a[2] === 3) add("copii", 2);
    if (a[3] === 1) add("copii", 2);
    if (a[3] === 2) add("copii", 3);
    if (a[5] === 0) { add("natura", 2); add("taste", 1); }
    if (a[5] === 1) add("adrenalina", 3);
    if (a[5] === 2) { add("natura", 1); add("adrenalina", 1); }
    add(["natura", "castele", "copii", "taste", "aproape"][a[6]] || "aproape", 3);
    return Object.entries(w).sort((x, y) => y[1] - x[1]).map(([c]) => c);
  };

  const itinerary = (() => {
    if (!result) return [];
    const d = t("quiz.days");
    const cats = rankedCats(answers);
    const used = new Set();
    const pick = (cat, n = 1) => {
      const pool = ATTRACTIONS
        .filter((x) => x.active !== false && x.categories.includes(cat) && !used.has(x.id))
        .sort((x, y) => ((y.featured ? 1 : 0) - (x.featured ? 1 : 0)) || (x.sort_order - y.sort_order));
      const chosen = pool.slice(0, n);
      chosen.forEach((x) => used.add(x.id));
      return chosen;
    };
    const name = (x) => x[`name_${lang}`] || x[`name_en`] || x.name;
    const days = [`${d.arrive} · ${pick("aproape", 1).map(name).join("") || name(ATTRACTIONS[0])}`];
    let ci = 0;
    while (days.length < result.nights - 1 && ci < cats.length) {
      const cat = cats[ci++];
      const picks = pick(cat, 2);
      if (picks.length) days.push(picks.map(name).join(" + "));
    }
    while (days.length < result.nights - 1) days.push(d.relax);
    days.push(d.leave);
    return days.slice(0, result.nights);
  })();

  const unit = units.find((u) => u.type === result?.unitType) || units[0];
  const unitName = unit ? (unit[`name_${lang}`] || unit.name_ro) : "";
  const price = result ? result.nights * (unit?.base_price ?? (result.unitType === "house" ? 580 : 70)) : 0;
  const wa = config?.whatsapp || "40721663181";

  return (
    <section id="quiz" className="py-24 md:py-32 bg-main text-cream relative overflow-hidden grain" data-testid="quiz-section">
      <div className="max-w-[900px] mx-auto px-6 md:px-12 relative z-10">
        <motion.p
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-sage flex items-center gap-3"
        >
          <span className="inline-block w-10 h-px bg-terracotta" />
          {t("quiz.eyebrow")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-5 font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          data-testid="quiz-title"
        >
          {t("quiz.title")}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-5 text-cream/70 font-light max-w-xl"
        >
          {t("quiz.sub")}
        </motion.p>

        <div className="mt-12 min-h-[380px]">
          <AnimatePresence mode="wait">
            {step === -1 && (
              <motion.div key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="flex flex-col items-start gap-7">
                <p className="font-serif italic text-xl md:text-2xl text-cream/85 max-w-xl leading-snug" data-testid="quiz-teaser">
                  {t("quiz.teaser")}
                </p>
                <div className="grid sm:grid-cols-3 gap-3 w-full" data-testid="quiz-perks">
                  {[CalendarCheck, MapPin, Wallet].map((Icon, i) => {
                    const perk = t("quiz.perks")[i];
                    return (
                      <div key={perk.t} className="rounded-2xl border border-cream/15 bg-cream/5 p-5" data-testid={`quiz-perk-${i}`}>
                        <Icon size={20} className="text-terracotta" />
                        <p className="mt-3 font-serif text-lg leading-tight">{perk.t}</p>
                        <p className="mt-1 text-xs text-cream/60 font-light leading-relaxed">{perk.d}</p>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={() => setStep(0)}
                  data-testid="quiz-start"
                  className="rounded-full bg-terracotta text-cream-light font-semibold px-8 py-4 hover:bg-terracotta/90 hover:scale-[1.03] transition-all duration-300 flex items-center gap-2"
                >
                  {t("quiz.start")} <ArrowRight size={18} />
                </button>
                <label className="flex items-center gap-2 text-sm text-cream/60 cursor-pointer">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="w-4 h-4 accent-[#C67A5C]" data-testid="quiz-consent" />
                  {t("quiz.consent")}
                </label>
              </motion.div>
            )}

            {step >= 0 && step < questions.length && (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -60 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="text-sm tracking-[0.2em] uppercase text-cream/50 font-semibold" data-testid="quiz-progress">
                  {t("quiz.step")} {step + 1} {t("quiz.of")} {questions.length}
                </p>
                <div className="mt-3 h-1 bg-cream/10 rounded-full overflow-hidden">
                  <motion.div className="h-full bg-terracotta" animate={{ width: `${((step + 1) / questions.length) * 100}%` }} transition={{ duration: 0.5 }} />
                </div>
                <h3 className="mt-8 font-serif text-2xl md:text-4xl leading-snug" data-testid="quiz-question">{questions[step].q}</h3>
                <div className="mt-8 grid gap-3">
                  {questions[step].a.map((opt, i) => (
                    <button
                      key={opt}
                      onClick={() => answer(i)}
                      data-testid={`quiz-option-${step}-${i}`}
                      className="text-left rounded-2xl border border-cream/15 bg-cream/5 px-6 py-4 text-cream/90 hover:bg-terracotta/20 hover:border-terracotta/50 transition-all duration-300"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button onClick={() => setStep(step - 1)} data-testid="quiz-back" className="mt-6 text-sm text-cream/50 hover:text-cream flex items-center gap-2 transition-colors">
                    <ArrowLeft size={15} /> {t("quiz.back")}
                  </button>
                )}
              </motion.div>
            )}

            {result && (
              <motion.div key="result" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} data-testid="quiz-result">
                <p className="text-xs tracking-[0.25em] uppercase font-bold text-sage">{t("quiz.result_eyebrow")}</p>
                <h3 className="mt-3 font-serif text-3xl md:text-4xl leading-snug max-w-xl text-cream" data-testid="quiz-result-title">
                  {t("concierge.result_title")}
                </h3>
                <div className="mt-4 flex items-end gap-4">
                  <span className="font-serif text-8xl md:text-9xl leading-none text-terracotta">{result.nights}</span>
                  <span className="font-serif italic text-2xl md:text-3xl text-cream/80 pb-3">{t("quiz.nights_word")}</span>
                </div>
                <p className="mt-4 font-serif italic text-lg text-cream/70 max-w-xl">
                  „{t(`quiz.titles.${result.nights}`)}"
                </p>

                <div className="mt-10 grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-sm tracking-[0.2em] uppercase font-bold text-cream/50 flex items-center gap-2">
                      <CalendarCheck size={15} /> {t("quiz.itinerary")}
                    </h4>
                    <ol className="mt-4 space-y-3">
                      {itinerary.map((d, i) => (
                        <li key={i} className="flex gap-4 text-sm text-cream/80 font-light">
                          <span className="font-serif italic text-terracotta text-lg leading-none shrink-0 w-8">{String(i + 1).padStart(2, "0")}</span>
                          {d}
                        </li>
                      ))}
                    </ol>
                  </div>
                  <div className="space-y-6">
                    <div className="rounded-2xl border border-cream/15 bg-cream/5 p-6">
                      <p className="text-xs tracking-[0.2em] uppercase font-bold text-cream/50">{t("quiz.recommended")}</p>
                      <p className="mt-2 font-serif text-2xl" data-testid="quiz-result-unit">{unitName}</p>
                      <p className="mt-4 text-xs tracking-[0.2em] uppercase font-bold text-cream/50">{t("quiz.estimate")}</p>
                      <p className="mt-1 font-serif text-3xl text-terracotta" data-testid="quiz-result-price">~{price} EUR</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button onClick={() => scrollToId("#rezervare")} data-testid="quiz-cta-check"
                        className="rounded-full bg-terracotta text-cream-light font-semibold px-6 py-3.5 hover:bg-terracotta/90 transition-all duration-300 text-sm">
                        {t("quiz.cta_check")}
                      </button>
                      <a href={waLink(wa, t("concierge.wa_msg").replace("{nights}", result.nights).replace("{plan}", itinerary.map((day, i) => `${i + 1}. ${day}`).join("\n")).replace("{unit}", unitName))}
                        target="_blank" rel="noreferrer" data-testid="quiz-cta-wa"
                        className="rounded-full border border-cream/30 text-cream font-semibold px-6 py-3.5 hover:border-terracotta hover:text-terracotta transition-all duration-300 text-sm text-center">
                        {t("concierge.cta")}
                      </a>
                    </div>
                    <button onClick={() => { setStep(-1); setAnswers([]); }} data-testid="quiz-restart"
                      className="text-sm text-cream/50 hover:text-cream flex items-center gap-2 transition-colors">
                      <RotateCcw size={14} /> {t("quiz.restart")}
                    </button>
                  </div>
                </div>
                <p className="mt-8 text-xs text-cream/40 flex items-center gap-2"><Sparkles size={13} /> Imperfect House · Râșnov</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
